/**
 * Fleet Health MCP App.
 *
 * Rewritten after the original shipped three independent faults, each of which
 * alone produced a useless panel:
 *
 *   1. It omitted the `resellerId` that OCS requires on both calls.
 *   2. It read `account.active`, but the live shape nests counts under
 *      `account → sponsor → esim → status`, so every total stayed 0.
 *   3. The HTML came from `ctx.env.ASSETS`, a Worker-only binding, so over
 *      stdio the panel was always the string "Dashboard unavailable".
 *
 * Net effect: `fleet_health` reported 100 eSIMs while `fleet_health_app`
 * rendered an empty dashboard, and nothing failed loudly.
 *
 * Now the data goes through the same normaliser as the prose tool, and the HTML
 * is rendered here from `@carrier/screens` — self-contained, no asset binding,
 * no CDN, identical on both transports.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import {
  registerAppTool,
  registerAppResource,
  RESOURCE_MIME_TYPE,
} from "@modelcontextprotocol/ext-apps/server";
import { fleetScreen, renderHtml, type FleetInput } from "@carrier/screens";
import { OcsClient } from "../client.js";
import type { ToolContext } from "../tools.js";
import { getDefaultResellerId } from "../tools.js";
import { toFleetInput } from "./fleet-data.js";

export const FLEET_APP_RESOURCE_URI = "ui://fleet-health-dashboard";

async function safeCall<T = unknown>(
  client: OcsClient,
  method: string,
  params: Record<string, unknown>,
): Promise<{ data: T | null; error: string | null }> {
  try {
    return { data: await client.call<T>(method, params), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Fetch and normalise the fleet view.
 *
 * `resellerId` is resolved first and passed to both calls — OCS returns nothing
 * useful without it, which is how the panel came to show zeros.
 */
export async function loadFleet(
  ctx: ToolContext,
  accountId?: number,
): Promise<FleetInput> {
  const token = await ctx.getUserToken(ctx.props.sub);
  const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
  const resellerId = await getDefaultResellerId(ctx.env, token).catch(() => undefined);

  const statusParams: Record<string, unknown> =
    accountId !== undefined
      ? { accountId }
      : resellerId !== undefined
        ? { resellerId }
        : {};
  const accountParams: Record<string, unknown> =
    resellerId !== undefined ? { resellerId } : {};

  const [status, accounts] = await Promise.all([
    safeCall(client, "esimStatusPerAccount", statusParams),
    safeCall(client, "listResellerAccount", accountParams),
  ]);

  return toFleetInput({
    status: status.data,
    accounts: accounts.data,
    statusError: status.error,
    accountsError: accounts.error,
  });
}

export function registerFleetHealthApp(server: McpServer, ctx: ToolContext): void {
  registerAppResource(
    server,
    "Fleet Health Dashboard",
    FLEET_APP_RESOURCE_URI,
    {
      description:
        "Fleet overview panel — eSIM status, per-account breakdown and low-balance alerts.",
    },
    async () => {
      // Rendered at read time so the panel always shows current state. The
      // resource read carries no arguments, so this is the whole-fleet view;
      // the tool's structuredContent carries any account filter for the model.
      let html: string;
      try {
        html = renderHtml(fleetScreen(await loadFleet(ctx)));
      } catch (err) {
        html = renderHtml({
          id: "fleet-health",
          title: "Fleet health",
          sections: [
            {
              kind: "note",
              tone: "critical",
              text: `Could not load fleet data: ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        });
      }
      return {
        contents: [
          {
            uri: FLEET_APP_RESOURCE_URI,
            mimeType: RESOURCE_MIME_TYPE,
            text: html,
            // Self-contained: no external origins, so no CSP exceptions.
          },
        ],
      };
    },
  );

  registerAppTool(
    server,
    "fleet_health_app",
    {
      title: "Fleet Health Dashboard",
      description:
        "Draw the fleet overview as an interactive panel: eSIM status, per-account breakdown and low-balance alerts. Returns structuredContent for the panel, so it only helps a client that can render MCP UI resources. Do NOT use this when the answer has to be readable text, or in any headless or scripted context — `fleet_health` returns the same data as prose.",
      inputSchema: {
        accountId: z.number().optional().describe("Filter to a specific account (omit for all)"),
      },
      annotations: { readOnlyHint: true },
      _meta: {
        ui: {
          resourceUri: FLEET_APP_RESOURCE_URI,
          visibility: ["model", "app"] as ["model", "app"],
        },
      },
    },
    async ({ accountId }) => {
      const input = await loadFleet(ctx, accountId);
      const screen = fleetScreen(input);
      const total = input.active + input.suspended + input.inventory + input.other;
      const lowBalanceCount = input.accounts.filter((a) => a.balance < 10).length;
      return {
        content: [
          {
            type: "text" as const,
            text:
              total === 0 && input.accounts.length === 0
                ? "Fleet view returned no eSIMs and no accounts — check the account scope and credentials."
                : `${total} eSIMs across ${input.accounts.length} account(s); ${input.active} active. Fleet Utilization: ${
                    total > 0 ? ((input.active / total) * 100).toFixed(1) : "0"
                  }%`,
          },
        ],
        structuredContent: {
          // `screen` is the shared model — the panel and `carrier dash` both
          // render this exact object, so the two cannot disagree.
          screen,
          // Flat fields are the original published contract. Kept so anything
          // already reading them keeps working; the fix was the data, not the shape.
          utilization: total > 0 ? Math.round((input.active / total) * 1000) / 10 : 0,
          totalActive: input.active,
          totalSuspended: input.suspended,
          totalInventory: input.inventory,
          totalOther: input.other,
          totalAccounts: input.accounts.length,
          lowBalanceCount,
          accountList: input.accounts,
        },
      };
    },
  );
}
